
import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { DataTable, Column } from "@/components/ui/data-table"
import { courseService } from "@/services/course.service"
import { Course } from "@/types/api"
import { Button } from "@/components/ui/button"
import { Edit2, Plus, Trash2, Loader2 } from "lucide-react"
import { useToast } from "@/hooks/use-toast"
import { Badge } from "@/components/ui/badge"
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog"

export default function CoursesPage() {
    const [courses, setCourses] = useState<Course[]>([])
    const [isLoading, setIsLoading] = useState(true)
    const [page, setPage] = useState(1)
    const [totalCount, setTotalCount] = useState(0)
    const [courseToDelete, setCourseToDelete] = useState<Course | null>(null)
    const [isDeleting, setIsDeleting] = useState(false)

    const { toast } = useToast()
    const navigate = useNavigate()
    const PAGE_SIZE = 10

    const fetchCourses = async () => {
        setIsLoading(true)
        try {
            const response = await courseService.getCourses({
                limit: PAGE_SIZE,
                offset: (page - 1) * PAGE_SIZE,
            })
            setCourses(response.results)
            setTotalCount(response.count)
        } catch (error) {
            console.error(error)
            toast({
                title: "Ошибка",
                description: "Не удалось загрузить курсы",
                variant: "destructive",
            })
        } finally {
            setIsLoading(false)
        }
    }

    useEffect(() => {
        fetchCourses()
    }, [page])

    const handleDelete = async () => {
        if (!courseToDelete) return
        
        setIsDeleting(true)
        try {
            await courseService.deleteCourse(courseToDelete.slug)
            toast({
                title: "Успешно",
                description: "Курс успешно удален",
            })
            setCourseToDelete(null)
            fetchCourses()
        } catch (error) {
            console.error(error)
            toast({
                title: "Ошибка",
                description: "Не удалось удалить курс",
                variant: "destructive",
            })
        } finally {
            setIsDeleting(false)
        }
    }

    const columns: Column<Course>[] = [
        {
            header: "Баннер",
            cell: (course) => (
                <div className="h-10 w-16 overflow-hidden rounded bg-gray-100">
                    {course.banner ? (
                        <img src={course.banner} alt={course.title} className="h-full w-full object-cover" />
                    ) : (
                        <div className="h-full w-full flex items-center justify-center text-xs text-gray-400">Нет</div>
                    )}
                </div>
            ),
        },
        {
            header: "Название",
            accessorKey: "title",
            cell: (course) => (
                <div>
                    <div className="font-medium">{course.title}</div>
                    <div className="text-xs text-gray-500 truncate max-w-[200px]">{course.slug}</div>
                </div>
            )
        },
        {
            header: "Статус",
            accessorKey: "is_active",
            cell: (course) => (
                <div className="flex flex-col gap-1">
                    <div className="flex flex-wrap gap-1">
                        <Badge variant={course.is_active ? "default" : "secondary"}>
                            {course.is_active ? "Активный" : "Черновик"}
                        </Badge>
                        {course.is_upcoming && (
                            <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                                Скоро
                            </Badge>
                        )}
                    </div>
                    {course.is_upcoming && course.release_date && (
                        <span className="text-xs text-gray-500">{course.release_date}</span>
                    )}
                </div>
            )
        },
        {
            header: "Цена",
            cell: (course) => (
                <div className="text-sm">
                    {course.price ? `$${course.price}` : <span className="text-gray-400">Бесплатно</span>}
                </div>
            )
        },
        {
            header: "Категории",
            cell: (course) => (
                <div className="flex flex-wrap gap-1">
                    {course.categories.slice(0, 2).map(cat => (
                        <Badge key={cat.id} variant="outline" className="text-xs">{cat.title}</Badge>
                    ))}
                    {course.categories.length > 2 && <span className="text-xs text-gray-500">+{course.categories.length - 2}</span>}
                </div>
            ),
        },
        {
            header: "Авторы",
            cell: (course) => (
                <div className="text-sm">
                    {course.authors.map(a => a.full_name).join(", ")}
                </div>
            )
        },
        {
            header: "Действия",
            className: "text-right",
            cell: (course) => (
                <div className="flex justify-end gap-2">
                    <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => navigate(`/courses/${course.slug}/edit`)}
                    >
                        <Edit2 className="h-4 w-4" />
                    </Button>
                    <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setCourseToDelete(course)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                        <Trash2 className="h-4 w-4" />
                    </Button>
                </div>
            ),
        },
    ]

    return (
        <div>
            <div className="mb-8 flex items-center justify-between">
                <div>
                    <h1 className="text-3xl font-bold text-gray-900">Курсы</h1>
                    <p className="text-gray-600 mt-1">Управление курсами и учебными планами.</p>
                </div>
                <Button onClick={() => navigate("/courses/new")}>
                    <Plus className="mr-2 h-4 w-4" />
                    Создать курс
                </Button>
            </div>

            <Card>
                <CardHeader>
                    <CardTitle>Список курсов</CardTitle>
                </CardHeader>
                <CardContent>
                    <DataTable
                        columns={columns}
                        data={courses}
                        isLoading={isLoading}
                        pageCount={Math.ceil(totalCount / PAGE_SIZE)}
                        currentPage={page}
                        onPageChange={setPage}
                    />
                </CardContent>
            </Card>

            <AlertDialog open={!!courseToDelete} onOpenChange={(open) => !open && setCourseToDelete(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Вы уверены?</AlertDialogTitle>
                        <AlertDialogDescription>
                            Это навсегда удалит курс "{courseToDelete?.title}". Это действие нельзя отменить.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel disabled={isDeleting}>Отмена</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={handleDelete}
                            disabled={isDeleting}
                            className="bg-red-600 hover:bg-red-700"
                        >
                            {isDeleting ? (
                                <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    Удаление...
                                </>
                            ) : (
                                "Удалить"
                            )}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    )
}
